/* ── Player Dashboard ─────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Mail,
  Lock,
  UserCircle2,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  LogOut,
  Sparkles,
  ArrowLeft,
  Trophy,
  Heart,
  X,
  Award,
  TrendingUp,
  TrendingDown,
  Minus,
  Camera,
  Settings,
  Zap,
} from "lucide-react";
import PlayerSettings from "./PlayerSettings";
import {
  groupBadgeDefs,
  BadgeCard,
  FamilyBadgeCard,
} from "../components/BadgeDisplay";
import CareerHighsSection, {
  fetchCareerHighs,
} from "../components/CareerHighsSection";
import PlayerCareerSection from "../components/PlayerCareerSection";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import LazyPhoto from "../components/LazyPhoto";
import HeadshotSelectorModal from "../components/HeadshotSelectorModal";
import CroppedAvatar from "../components/CroppedAvatar";

const POSITIONS = [
  { key: "PG", label: "Point Guard" },
  { key: "SG", label: "Shooting Guard" },
  { key: "SF", label: "Small Forward" },
  { key: "PF", label: "Power Forward" },
  { key: "C", label: "Center" },
];

const AGE_RANGES = ["17-20", "21-25", "26-30", "31-35", "36-40", "41+"];

const FEET_OPTIONS = [4, 5, 6, 7];
const INCH_OPTIONS = Array.from({ length: 12 }, (_, i) => i);

const TABS = [
  { id: "history", label: "Career", icon: Trophy },
  { id: "freeagency", label: "Free Agency", icon: ShieldCheck },
  { id: "saved", label: "Saved Photos", icon: Heart },
  { id: "settings", label: "Settings", icon: Settings },
];

const heightToInches = (feet, inches) => {
  if (feet === "" || inches === "") return null;
  return parseInt(feet, 10) * 12 + parseInt(inches, 10);
};

const inchesToHeight = (totalInches) => {
  if (totalInches == null) return { feet: "", inches: "" };
  return { feet: Math.floor(totalInches / 12), inches: totalInches % 12 };
};

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
      <Icon size={20} className="text-orange-500" />
    </div>
    <h2 className="text-2xl font-semibold tracking-tight dark:text-white">
      {title}
    </h2>
  </div>
);

// Small pill showing a team's name in its own colors. Falls back to plain
// gray text if the team has no color data (getTeamStyle already defaults
// to gray, so this always renders something readable).
const TeamBadge = ({ team, size = "sm" }) => {
  if (!team?.name) return null;
  const style = getTeamStyle(team);
  const textColor = getTextColorForAccent(style.primaryHex, isDarkModeActive());
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold shrink-0 ${
        size === "sm" ? "text-xs px-2.5 py-1" : "text-sm px-3 py-1.5"
      }`}
      style={{ ...style.headerStyle, color: textColor }}
    >
      {team.name}
    </span>
  );
};

// Generic confirm modal — same visual language as Register.jsx's ConfirmModal.
// Worth extracting to src/modals/ConfirmModal.jsx if both end up needing it.
const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel,
  tone = "orange",
  loading,
  onConfirm,
  onCancel,
}) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
        >
          <div className="p-8">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles
                className={tone === "red" ? "text-red-500" : "text-amber-500"}
                size={26}
              />
              <h3 className="text-2xl font-semibold dark:text-white">
                {title}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {message}
            </p>
          </div>
          <div className="border-t dark:border-gray-800 flex">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 py-4 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`flex-1 py-4 text-sm font-semibold text-white transition-colors disabled:opacity-70 ${
                tone === "red"
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-orange-600 hover:bg-orange-700"
              }`}
            >
              {loading ? "Saving..." : confirmLabel}
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

// Shown when there's no authenticated player session yet.
// Two modes: returning players sign in with their password; new players
// request an account, which emails them a temporary password to sign in with.
const PlayerLoginGate = () => {
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "signup"

  // Shared
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Login-only
  const [password, setPassword] = useState("");

  // Signup-only
  const [name, setName] = useState("");
  const [signedUp, setSignedUp] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(email.trim().toLowerCase(), password);
      // AuthContext's onAuthStateChange picks up the session from here.
    } catch (err) {
      setError("Incorrect email or password. Double check and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    const { data, error: fnError } = await supabase.functions.invoke(
      "create-player-account",
      { body: { email: email.trim().toLowerCase(), name: name.trim() } },
    );
    setSubmitting(false);
    if (fnError || data?.error) {
      setError(
        data?.error ||
          "Couldn't create your account. Double check your email and try again.",
      );
    } else {
      setSignedUp(true);
    }
  };

  if (signedUp) {
    return (
      <div className="max-w-7xl mx-auto py-20 px-6 text-center">
        <div className="flex justify-center mb-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-100 dark:bg-emerald-950">
            <CheckCircle2 className="text-emerald-600" size={32} />
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight dark:text-white mb-3">
          Check your email
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          We sent a temporary password to{" "}
          <span className="font-medium">{email}</span>. Sign in with it below —
          you'll be asked to set your own password right away.
        </p>
        <button
          onClick={() => {
            setSignedUp(false);
            setMode("login");
          }}
          className="text-orange-600 dark:text-orange-400 font-medium hover:underline"
        >
          Go to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto py-20 px-6">
      <div className="text-center mb-10">
        <div className="flex justify-center mb-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-orange-500/10">
            <UserCircle2 className="text-orange-500" size={32} />
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight dark:text-white mb-2">
          Player Dashboard
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {mode === "login"
            ? "Sign in to your account"
            : "Create your player account"}
        </p>
      </div>

      <div className="flex mb-8 bg-gray-100 dark:bg-gray-800 rounded-2xl p-1">
        <button
          onClick={() => {
            setMode("login");
            setError(null);
          }}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            mode === "login"
              ? "bg-white dark:bg-gray-900 text-orange-600 dark:text-orange-400 shadow-sm"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          Sign In
        </button>
        <button
          onClick={() => {
            setMode("signup");
            setError(null);
          }}
          className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            mode === "signup"
              ? "bg-white dark:bg-gray-900 text-orange-600 dark:text-orange-400 shadow-sm"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          First Time
        </button>
      </div>

      {mode === "login" ? (
        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-white">
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-white">
              PASSWORD
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>

          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-bold hover:brightness-110 active:scale-[0.985] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
          >
            <Lock size={18} />
            {submitting ? "Signing in..." : "Sign In"}
          </button>

          <p className="text-center text-sm">
            <Link
              to="/forgot-password"
              className="text-gray-500 dark:text-gray-400 hover:underline"
            >
              Forgot password?
            </Link>
          </p>
        </form>
      ) : (
        <form onSubmit={handleSignup} className="space-y-5">
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-white">
              FULL NAME
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Full name"
              className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2 dark:text-white">
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>

          {error && (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-bold hover:brightness-110 active:scale-[0.985] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
          >
            <Mail size={18} />
            {submitting ? "Creating account..." : "Create Account"}
          </button>
        </form>
      )}
    </div>
  );
};

// Forced on first login after a player's account was created with an
// admin-issued temporary password. Blocks the rest of the dashboard until cleared.
const ForcePasswordChange = () => {
  const { updatePassword } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updatePassword(newPassword);
    } catch (err) {
      setError("Couldn't update your password. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-20 px-6">
      <div className="text-center mb-10">
        <div className="flex justify-center mb-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-amber-500/10">
            <Lock className="text-amber-500" size={32} />
          </div>
        </div>
        <h1 className="text-3xl font-bold tracking-tight dark:text-white mb-2">
          Set Your Password
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          For your security, choose a new password to replace the temporary one.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold mb-2 dark:text-white">
            NEW PASSWORD
          </label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2 dark:text-white">
            CONFIRM PASSWORD
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
          />
        </div>

        {error && (
          <div className="flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-4 text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-bold hover:brightness-110 active:scale-[0.985] transition-all disabled:opacity-70"
        >
          {submitting ? "Saving..." : "Set Password & Continue"}
        </button>
      </form>
    </div>
  );
};

const PlayerDashboard = () => {
  const { user, signOut } = useAuth();

  const [activeTab, setActiveTab] = useState("history");

  const [freeAgentRow, setFreeAgentRow] = useState(null);
  const [formerTeamId, setFormerTeamId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [badgeDefs, setBadgeDefs] = useState([]);
  const [careerStats, setCareerStats] = useState(null);
  const [unlockedBadgeKeys, setUnlockedBadgeKeys] = useState(new Set());

  const [stockHistory, setStockHistory] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockLoaded, setStockLoaded] = useState(false);

  const [careerHighs, setCareerHighs] = useState([]);
  const [ddGames, setDdGames] = useState([]);
  const [tdGames, setTdGames] = useState([]);
  const [careerHighsLoading, setCareerHighsLoading] = useState(false);
  const [careerHighsLoaded, setCareerHighsLoaded] = useState(false);

  const [linkedPlayers, setLinkedPlayers] = useState([]); // players rows now tied to this profile, most recent first
  const [seasonStats, setSeasonStats] = useState({}); // { [season_id]: { gp, pts, reb, ast } }
  const [myClaims, setMyClaims] = useState([]); // pending/rejected/approved claims by this profile

  const [claimQuery, setClaimQuery] = useState("");
  const [claimResults, setClaimResults] = useState([]);
  const [claimSearching, setClaimSearching] = useState(false);
  const [claimSubmittingId, setClaimSubmittingId] = useState(null);

  const [selectedPositions, setSelectedPositions] = useState([]);
  const [note, setNote] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");

  const [modal, setModal] = useState(null); // "join" | "withdraw" | null
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const [savedPhotos, setSavedPhotos] = useState([]);
  const [savedPhotosLoading, setSavedPhotosLoading] = useState(false);
  const [savedPhotosLoaded, setSavedPhotosLoaded] = useState(false);
  const [savedImgErrors, setSavedImgErrors] = useState({});
  const [unsavingId, setUnsavingId] = useState(null);

  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarCrop, setAvatarCrop] = useState(null);
  const [headshotModalOpen, setHeadshotModalOpen] = useState(false);

  // ── Core dashboard data: free agent status, linked player history, claims ──
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setLoading(true);
      setError(null);

      const [
        { data: faRow, error: faErr },
        { data: linked },
        { data: claims },
        { data: badgeDefsData, error: badgeDefsErr },
        { data: careerStatsData, error: careerStatsErr },
        { data: unlockedRows, error: unlockedErr },
        { data: profileRow, error: profileErr },
      ] = await Promise.all([
        supabase
          .from("free_agents")
          .select("*")
          .eq("profile_id", user.id)
          .maybeSingle(),
        supabase
          .from("players")
          .select(
            "id, season_id, team_id, seasons(id, name), teams(name, primary_color, secondary_color, gradient)",
          )
          .eq("profile_id", user.id)
          .not("team_id", "is", null)
          .order("season_id", { ascending: false }),
        supabase
          .from("player_claims")
          .select(
            "id, player_id, status, requested_at, rejection_reason, players(seasons(name), teams(name))",
          )
          .eq("profile_id", user.id)
          .order("requested_at", { ascending: false }),
        supabase
          .from("badges")
          .select("*")
          .order("threshold", { ascending: true }),
        supabase
          .from("player_career_stats")
          .select("*")
          .eq("profile_id", user.id)
          .maybeSingle(),
        supabase
          .from("player_badges")
          .select("badge_key")
          .eq("profile_id", user.id),
        supabase
          .from("profiles")
          .select("avatar_url, avatar_crop")
          .eq("id", user.id)
          .maybeSingle(),
      ]);

      if (faErr) {
        setError("Couldn't load your free agent status.");
      } else if (faRow) {
        setFreeAgentRow(faRow);
        setSelectedPositions(faRow.positions || []);
        setNote(faRow.note || "");
        setAgeRange(faRow.age_range || "");
        const { feet, inches } = inchesToHeight(faRow.height_inches);
        setHeightFeet(feet === "" ? "" : String(feet));
        setHeightInches(inches === "" ? "" : String(inches));
      }

      if (badgeDefsErr) console.error("badges fetch failed:", badgeDefsErr);
      if (careerStatsErr)
        console.error("career stats fetch failed:", careerStatsErr);
      if (unlockedErr)
        console.error("player_badges fetch failed:", unlockedErr);
      if (profileErr) console.error("profile fetch failed:", profileErr);

      setLinkedPlayers(linked || []);
      setMyClaims(claims || []);

      setBadgeDefs(badgeDefsData || []);
      setCareerStats(careerStatsData || null);
      setUnlockedBadgeKeys(
        new Set((unlockedRows || []).map((r) => r.badge_key)),
      );
      setAvatarUrl(profileRow?.avatar_url ?? null);
      setAvatarCrop(profileRow?.avatar_crop ?? null);

      // Most recent team, derived from the same query — no second round trip.
      setFormerTeamId(linked?.[0]?.team_id ?? null);

      if (linked?.length) {
        const playerIds = linked.map((p) => p.id);

        const { data: statRows } = await supabase
          .from("game_stats")
          .select(
            "player_id, two_pm, three_pm, ft_made, rebounds, assists, personal_fouls, technical_fouls, games!inner(counts_in_standings)",
          )
          .in("player_id", playerIds)
          .eq("games.counts_in_standings", true);

        const seasonByPlayerId = Object.fromEntries(
          linked.map((p) => [p.id, p]),
        );
        const grouped = {};

        (statRows || []).forEach((row) => {
          // ── DNP Filter: Skip games where all stats are zero ──
          const pts =
            (row.two_pm ?? 0) * 2 +
            (row.three_pm ?? 0) * 3 +
            (row.ft_made ?? 0);
          const isDNP =
            pts === 0 &&
            (row.rebounds ?? 0) === 0 &&
            (row.assists ?? 0) === 0 &&
            (row.three_pm ?? 0) === 0 &&
            (row.two_pm ?? 0) === 0 &&
            (row.ft_made ?? 0) === 0 &&
            (row.personal_fouls ?? 0) === 0 &&
            (row.technical_fouls ?? 0) === 0;
          if (isDNP) return;

          const player = seasonByPlayerId[row.player_id];
          if (!player) return;
          const key = player.season_id;
          if (!grouped[key]) {
            grouped[key] = {
              gp: 0,
              pts: 0,
              reb: 0,
              ast: 0,
              three_pm: 0,
              two_pm: 0,
              ft_made: 0,
            };
          }
          const s = grouped[key];
          s.gp += 1;
          s.pts += pts;
          s.reb += row.rebounds || 0;
          s.ast += row.assists || 0;
          s.three_pm += row.three_pm || 0;
          s.two_pm += row.two_pm || 0;
          s.ft_made += row.ft_made || 0;
        });

        setSeasonStats(grouped);
      }

      setLoading(false);
    };

    load();
  }, [user]);

  // ── Career Highs: loaded lazily when the history tab is first opened ──
  const loadCareerHighs = useCallback(async () => {
    if (!user || linkedPlayers.length === 0) return;
    setCareerHighsLoading(true);
    const playerIds = linkedPlayers.map((p) => p.id);
    const {
      highs,
      ddGames: dd,
      tdGames: td,
    } = await fetchCareerHighs(supabase, playerIds);
    setCareerHighs(highs);
    setDdGames(dd);
    setTdGames(td);
    setCareerHighsLoading(false);
    setCareerHighsLoaded(true);
  }, [user, linkedPlayers]);

  useEffect(() => {
    if (
      activeTab === "history" &&
      !careerHighsLoaded &&
      linkedPlayers.length > 0
    ) {
      loadCareerHighs();
    }
  }, [activeTab, careerHighsLoaded, linkedPlayers, loadCareerHighs]);

  const loadStockHistory = useCallback(async () => {
    if (!user) return;
    setStockLoading(true);
    const { data, error: stockErr } = await supabase
      .from("player_stock_history")
      .select("game_id, game_date, season_id, game_score, stock_value")
      .eq("profile_id", user.id)
      .order("game_date", { ascending: true });
    if (stockErr) console.error("stock history fetch failed:", stockErr);
    setStockHistory(data || []);
    setStockLoading(false);
    setStockLoaded(true);
  }, [user]);

  useEffect(() => {
    if (activeTab === "history" && !stockLoaded) {
      loadStockHistory();
    }
  }, [activeTab, stockLoaded, loadStockHistory]);

  // ── Saved photos: loaded lazily, only once the tab is opened ──
  const loadSavedPhotos = useCallback(async () => {
    if (!user) return;
    setSavedPhotosLoading(true);
    const { data } = await supabase
      .from("saved_photos")
      .select(
        "photo_id, created_at, photo:photos(id, filename, thumbnail_url, full_url, drive_file_id, game:games(team1:teams!games_team1_id_fkey(name), team2:teams!games_team2_id_fkey(name)))",
      )
      .eq("profile_id", user.id)
      .order("created_at", { ascending: false });
    setSavedPhotos((data || []).map((r) => r.photo).filter(Boolean));
    setSavedPhotosLoading(false);
    setSavedPhotosLoaded(true);
  }, [user]);

  useEffect(() => {
    if (activeTab === "saved" && !savedPhotosLoaded) {
      loadSavedPhotos();
    }
  }, [activeTab, savedPhotosLoaded, loadSavedPhotos]);

  const unsavePhoto = async (photoId) => {
    if (!user) return;
    setUnsavingId(photoId);
    await supabase
      .from("saved_photos")
      .delete()
      .eq("profile_id", user.id)
      .eq("photo_id", photoId);
    setSavedPhotos((prev) => prev.filter((p) => p.id !== photoId));
    setUnsavingId(null);
  };

  const togglePosition = (key) => {
    setSelectedPositions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const isActiveMember = freeAgentRow?.is_active;
  const canSubmit = ageRange !== "";
  const mostRecentTeam = linkedPlayers[0]?.teams;

  const handleJoin = async () => {
    setSaving(true);
    setError(null);
    const { data, error: joinErr } = await supabase
      .from("free_agents")
      .upsert(
        {
          profile_id: user.id,
          positions: selectedPositions,
          note: note.trim() || null,
          age_range: ageRange,
          height_inches: heightToInches(heightFeet, heightInches),
          former_team_id: formerTeamId,
          is_active: true,
          withdrawn_at: null,
        },
        { onConflict: "profile_id" },
      )
      .select()
      .single();

    setSaving(false);
    if (joinErr) {
      setError("Couldn't save your free agent status. Try again.");
      return;
    }
    setFreeAgentRow(data);
    setModal(null);
    setSuccessMsg("You're on the list for when the season wraps up.");
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const handleWithdraw = async () => {
    setSaving(true);
    setError(null);
    const { data, error: withdrawErr } = await supabase
      .from("free_agents")
      .update({ is_active: false, withdrawn_at: new Date().toISOString() })
      .eq("profile_id", user.id)
      .select()
      .single();

    setSaving(false);
    if (withdrawErr) {
      setError("Couldn't remove you from the list. Try again.");
      return;
    }
    setFreeAgentRow(data);
    setModal(null);
    setSuccessMsg("You've been removed from the free agent pool.");
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const searchUnclaimedPlayers = async () => {
    if (!claimQuery.trim()) return;
    setClaimSearching(true);
    const { data } = await supabase
      .from("players")
      .select("id, name, season_id, seasons(name), teams(name)")
      .is("profile_id", null)
      .ilike("name", `%${claimQuery.trim()}%`)
      .order("season_id", { ascending: false })
      .limit(20);
    setClaimResults(data || []);
    setClaimSearching(false);
  };

  const submitClaim = async (playerId) => {
    setClaimSubmittingId(playerId);
    const { error: claimErr } = await supabase
      .from("player_claims")
      .insert({ profile_id: user.id, player_id: playerId });
    setClaimSubmittingId(null);
    if (claimErr) {
      setError(
        claimErr.code === "23505"
          ? "Someone already submitted a claim for that record."
          : "Couldn't submit your claim. Try again.",
      );
      return;
    }
    setClaimResults((prev) => prev.filter((p) => p.id !== playerId));
    setSuccessMsg(
      "Claim sent to the Commissioner for review — you'll see it here once it's approved.",
    );
    setTimeout(() => setSuccessMsg(null), 4000);
    const { data } = await supabase
      .from("player_claims")
      .select(
        "id, player_id, status, requested_at, rejection_reason, players(seasons(name), teams(name))",
      )
      .eq("profile_id", user.id)
      .order("requested_at", { ascending: false });
    setMyClaims(data || []);
  };

  if (!user) {
    return <PlayerLoginGate />;
  }

  if (user.mustChangePassword) {
    return <ForcePasswordChange />;
  }

  return (
    <div className="max-w-7xl mx-auto px-2 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setHeadshotModalOpen(true)}
            className="relative group shrink-0"
            title="Change your headshot"
          >
            {avatarUrl ? (
              <CroppedAvatar
                avatarUrl={avatarUrl}
                avatarCrop={avatarCrop}
                size={56}
              />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <UserCircle2 size={28} className="text-gray-400" />
              </div>
            )}
            <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
              <Camera
                size={16}
                className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </button>
          <div>
            <h1 className="text-4xl font-bold tracking-tighter dark:text-white mb-1">
              Your Dashboard
            </h1>
            <p className="text-gray-500 dark:text-gray-400">{user.name}</p>
          </div>
        </div>
        <button
          onClick={signOut}
          className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
        >
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <AnimatePresence>
        {successMsg && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 flex items-center gap-3 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 p-5 text-emerald-700 dark:text-emerald-300"
          >
            <CheckCircle2 size={20} className="shrink-0" />
            {successMsg}
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 p-5 text-red-700 dark:text-red-400">
          <AlertTriangle size={20} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-orange-600 to-amber-600 text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Careers tab ── */}
      {activeTab === "history" && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-gray-800">
          <SectionHeader icon={Trophy} title="Your Career" />

          {loading ? (
            <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
          ) : linkedPlayers.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                No seasons linked to your account yet.
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Played before creating an account? Head to the{" "}
                <button
                  onClick={() => setActiveTab("claim")}
                  className="text-orange-600 dark:text-orange-400 font-medium hover:underline"
                >
                  Claim a Past Season
                </button>{" "}
                tab.
              </p>
            </div>
          ) : (
            <PlayerCareerSection
              seasons={linkedPlayers}
              seasonStats={seasonStats}
              emptyAction={
                <p>
                  Played before creating an account?{" "}
                  <button
                    onClick={() => setActiveTab("claim")}
                    className="text-orange-600 dark:text-orange-400 font-medium hover:underline"
                  >
                    Claim a Past Season
                  </button>
                </p>
              }
            />
          )}

          <br className="my-8 border-gray-200 dark:border-gray-800" />
          <SectionHeader icon={Zap} title="Career Highs" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Your all-time single-game peak performances across every season.
          </p>
          <CareerHighsSection
            careerHighs={careerHighs}
            ddGames={ddGames}
            tdGames={tdGames}
            loading={careerHighsLoading}
          />

          <br className="my-8 border-gray-200 dark:border-gray-800" />
          <SectionHeader icon={Award} title="Career Badges" />

          {loading ? (
            <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
          ) : !careerStats ? (
            <div className="text-center py-10">
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                No career stats linked to your account yet.
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Played before creating an account? Head to the{" "}
                <button
                  onClick={() => setActiveTab("claim")}
                  className="text-orange-600 dark:text-orange-400 font-medium hover:underline"
                >
                  Claim a Past Season
                </button>{" "}
                tab to unlock your history.
              </p>
            </div>
          ) : (
            <>
              {(() => {
                const { families, standalone } = groupBadgeDefs(badgeDefs);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {standalone.map((badge) => (
                      <BadgeCard
                        key={badge.key}
                        badge={badge}
                        unlocked={unlockedBadgeKeys.has(badge.key)}
                        currentValue={careerStats?.[badge.stat_key] ?? 0}
                      />
                    ))}
                    {Object.entries(families).map(([familyKey, tiers]) => (
                      <FamilyBadgeCard
                        key={familyKey}
                        tiers={tiers}
                        unlockedKeys={unlockedBadgeKeys}
                        currentValue={careerStats?.[tiers[0].stat_key] ?? 0}
                      />
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          <br className="my-8 border-gray-200 dark:border-gray-800" />
          <SectionHeader icon={TrendingUp} title="My Stock" />

          {stockLoading ? (
            <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
          ) : stockHistory.length < 2 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                Not enough games logged yet to chart your stock.
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Your value starts moving after your first couple of games.
              </p>
            </div>
          ) : (
            (() => {
              const latest = stockHistory[stockHistory.length - 1];
              const prev = stockHistory[stockHistory.length - 2];
              const delta = latest.stock_value - prev.stock_value;
              const trendUp = delta > 0.05;
              const trendDown = delta < -0.05;

              return (
                <>
                  <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 mb-1">
                        CURRENT STOCK VALUE
                      </p>
                      <p className="text-4xl font-bold tracking-tight dark:text-white">
                        {latest.stock_value.toFixed(1)}
                      </p>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                        trendUp
                          ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                          : trendDown
                            ? "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {trendUp ? (
                        <TrendingUp size={16} />
                      ) : trendDown ? (
                        <TrendingDown size={16} />
                      ) : (
                        <Minus size={16} />
                      )}
                      {delta > 0 ? "+" : ""}
                      {delta.toFixed(1)} last game
                    </div>
                  </div>

                  <div className="h-64 -ml-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stockHistory}>
                        <XAxis dataKey="game_date" hide />
                        <ReferenceLine
                          y={100}
                          stroke="#d1d5db"
                          strokeDasharray="4 4"
                        />
                        <Tooltip
                          formatter={(value) => [value.toFixed(1), "Stock"]}
                          labelFormatter={(label) =>
                            new Date(label).toLocaleDateString()
                          }
                          contentStyle={{
                            borderRadius: 12,
                            border: "none",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="stock_value"
                          stroke="#f97316"
                          strokeWidth={2.5}
                          dot={false}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <p className="text-xs text-gray-400 mt-4 text-center">
                    Everyone starts at 100. Your value moves based on how each
                    game compares to your own career average — hot streaks push
                    it up, cold ones pull it down.
                  </p>
                </>
              );
            })()
          )}

          <br className="my-8 border-gray-200 dark:border-gray-800" />
          <SectionHeader icon={UserCircle2} title="Claim a Past Season" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Played in a previous season before creating an account? Search your
            name and request to link it. The Commissioner reviews every claim
            before it's linked.
          </p>

          <div className="flex gap-2 mb-6">
            <input
              value={claimQuery}
              onChange={(e) => setClaimQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchUnclaimedPlayers()}
              placeholder="Search by name"
              className="flex-1 px-5 py-3 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              onClick={searchUnclaimedPlayers}
              disabled={claimSearching}
              className="px-6 py-3 bg-gray-900 dark:bg-gray-800 text-white rounded-2xl font-semibold disabled:opacity-60"
            >
              {claimSearching ? "Searching..." : "Search"}
            </button>
          </div>

          {claimResults.length > 0 && (
            <div className="space-y-3 mb-6">
              {claimResults.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between rounded-2xl border border-gray-100 dark:border-gray-800 p-4"
                >
                  <div>
                    <p className="font-medium dark:text-white">{r.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {r.seasons?.name} · {r.teams?.name}
                    </p>
                  </div>
                  <button
                    onClick={() => submitClaim(r.id)}
                    disabled={claimSubmittingId === r.id}
                    className="text-sm font-semibold text-orange-600 dark:text-orange-400 hover:underline disabled:opacity-60"
                  >
                    {claimSubmittingId === r.id ? "Sending..." : "This is me"}
                  </button>
                </div>
              ))}
            </div>
          )}

          {myClaims.length > 0 && (
            <div className="space-y-2">
              {myClaims.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between text-sm py-2 border-t dark:border-gray-800"
                >
                  <span className="text-gray-600 dark:text-gray-400">
                    {c.players?.seasons?.name} · {c.players?.teams?.name}
                  </span>
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      c.status === "approved"
                        ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                        : c.status === "rejected"
                          ? "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400"
                          : "bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400"
                    }`}
                  >
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Free Agency tab ── */}
      {activeTab === "freeagency" && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-gray-800">
          <SectionHeader icon={ShieldCheck} title="Free Agent Pool" />

          {loading ? (
            <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
          ) : isActiveMember ? (
            <div>
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium mb-6">
                <CheckCircle2 size={18} />
                You're on the list for next season
              </div>

              {mostRecentTeam?.name && (
                <div className="flex items-center gap-2 text-sm mb-6">
                  <span className="text-gray-500 dark:text-gray-400">
                    Listed as coming from
                  </span>
                  <TeamBadge team={mostRecentTeam} />
                </div>
              )}

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    AGE RANGE
                  </label>
                  <select
                    value={ageRange}
                    onChange={(e) => setAgeRange(e.target.value)}
                    className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  >
                    <option value="">Select...</option>
                    {AGE_RANGES.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    HEIGHT{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      className="w-full px-3 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">ft</option>
                      {FEET_OPTIONS.map((f) => (
                        <option key={f} value={f}>
                          {f}'
                        </option>
                      ))}
                    </select>
                    <select
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      className="w-full px-3 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">in</option>
                      {INCH_OPTIONS.map((i) => (
                        <option key={i} value={i}>
                          {i}"
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-semibold mb-3 dark:text-white">
                POSITIONS
              </label>
              <div className="flex flex-wrap gap-2 mb-6">
                {POSITIONS.map((pos) => {
                  const active = selectedPositions.includes(pos.key);
                  return (
                    <button
                      key={pos.key}
                      onClick={() => togglePosition(pos.key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        active
                          ? "bg-orange-600 text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pos.label}
                    </button>
                  );
                })}
              </div>

              <label className="block text-sm font-semibold mb-2 dark:text-white">
                NOTE FOR RECRUITING CAPTAINS{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. Available most Sundays, prefer a competitive team"
                className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all mb-8"
              />

              <div className="flex gap-4">
                <button
                  onClick={() => setModal("join")}
                  disabled={!canSubmit}
                  className="flex-1 py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-semibold hover:brightness-110 active:scale-[0.985] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setModal("withdraw")}
                  className="px-6 py-4 text-red-600 dark:text-red-400 border-2 border-red-100 dark:border-red-900 rounded-2xl font-semibold hover:bg-red-50 dark:hover:bg-red-950/30 transition-all"
                >
                  Withdraw
                </button>
              </div>

              <p className="text-xs text-gray-400 mt-5 text-center">
                This stays private and only becomes visible to teams once
                registration for the next season opens.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Thinking about a new team next season? Join the free agent pool
                and teams will be able to reach out once the current season
                wraps up.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    AGE RANGE
                  </label>
                  <select
                    value={ageRange}
                    onChange={(e) => setAgeRange(e.target.value)}
                    className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  >
                    <option value="">Select...</option>
                    {AGE_RANGES.map((range) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 dark:text-white">
                    HEIGHT{" "}
                    <span className="text-gray-400 font-normal">
                      (optional)
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      className="w-full px-3 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">ft</option>
                      {FEET_OPTIONS.map((f) => (
                        <option key={f} value={f}>
                          {f}'
                        </option>
                      ))}
                    </select>
                    <select
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      className="w-full px-3 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">in</option>
                      {INCH_OPTIONS.map((i) => (
                        <option key={i} value={i}>
                          {i}"
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <label className="block text-sm font-semibold mb-3 dark:text-white">
                POSITIONS{" "}
                <span className="text-gray-400 font-normal">
                  (select any that apply)
                </span>
              </label>
              <div className="flex flex-wrap gap-2 mb-6">
                {POSITIONS.map((pos) => {
                  const active = selectedPositions.includes(pos.key);
                  return (
                    <button
                      key={pos.key}
                      onClick={() => togglePosition(pos.key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        active
                          ? "bg-orange-600 text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pos.label}
                    </button>
                  );
                })}
              </div>

              <label className="block text-sm font-semibold mb-2 dark:text-white">
                NOTE FOR RECRUITING CAPTAINS{" "}
                <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. Available most Sundays, prefer a competitive team"
                className="w-full px-5 py-3.5 border-2 border-gray-200 dark:border-gray-800 rounded-2xl dark:bg-gray-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all mb-8"
              />

              <button
                onClick={() => setModal("join")}
                disabled={!canSubmit}
                className="w-full py-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-2xl font-bold hover:brightness-110 active:scale-[0.985] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="rotate-180" size={18} />
                Join Next Season's Free Agent Pool
              </button>
              {!canSubmit && (
                <p className="text-xs text-gray-400 text-center mt-3">
                  Select your age range to continue
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Saved Photos tab ── */}
      {activeTab === "saved" && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-gray-800">
          <SectionHeader icon={Heart} title="Saved Photos" />

          {savedPhotosLoading ? (
            <div className="h-40 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse" />
          ) : savedPhotos.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500 dark:text-gray-400 mb-1">
                No saved photos yet.
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Tap the heart icon on any photo in the{" "}
                <Link
                  to="/gallery"
                  className="text-orange-600 dark:text-orange-400 font-medium hover:underline"
                >
                  Gallery
                </Link>{" "}
                to save it here.
              </p>
            </div>
          ) : (
            <div className="columns-2 sm:columns-3 gap-2 space-y-2">
              {savedPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative break-inside-avoid rounded-lg overflow-hidden group"
                >
                  <LazyPhoto
                    photo={photo}
                    imgErrors={savedImgErrors}
                    setImgErrors={setSavedImgErrors}
                  />
                  {photo.game && (
                    <div className="absolute bottom-1.5 left-1.5 right-1.5">
                      <div className="bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded truncate">
                        {photo.game.team1?.name} vs {photo.game.team2?.name}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => unsavePhoto(photo.id)}
                    disabled={unsavingId === photo.id}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition disabled:opacity-60"
                    title="Remove from saved"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Settings tab ── */}
      {activeTab === "settings" && (
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-gray-800">
          <PlayerSettings />
        </div>
      )}

      <HeadshotSelectorModal
        isOpen={headshotModalOpen}
        onClose={() => setHeadshotModalOpen(false)}
        userId={user.id}
        onAvatarUpdated={(newUrl, newCrop) => {
          setAvatarUrl(newUrl);
          setAvatarCrop(newCrop);
        }}
      />

      <ConfirmModal
        isOpen={modal === "join"}
        title={isActiveMember ? "Save changes?" : "Join the free agent pool?"}
        message={
          isActiveMember
            ? "Your positions and note will be updated. This still won't be visible to anyone until the season ends."
            : "You'll be added to next season's free agent list. It stays completely private — no one, including team captains, can see it until the current season officially ends."
        }
        confirmLabel={isActiveMember ? "Save" : "Confirm"}
        loading={saving}
        onConfirm={handleJoin}
        onCancel={() => setModal(null)}
      />

      <ConfirmModal
        isOpen={modal === "withdraw"}
        title="Withdraw from the pool?"
        message="You'll be removed from the free agent list. You can rejoin anytime before next season starts."
        confirmLabel="Withdraw"
        tone="red"
        loading={saving}
        onConfirm={handleWithdraw}
        onCancel={() => setModal(null)}
      />
    </div>
  );
};

export default PlayerDashboard;
